<template>
  <div class="landing-page">
    <transition name="home-subview" mode="out-in">
      <div v-if="isLoading" key="loading" class="home-subview">
        <HomeLoadingOverlay />
      </div>
      <div v-else-if="showProjectTypePicker" key="picker" class="home-subview">
        <HomeProjectTypePicker
          :cards="projectTypeCards"
          @back="cancelProjectTypePicker"
          @select="selectProjectType"
        />
      </div>
      <div v-else key="landing" class="home-subview home-subview--landing">
        <HomeNavBar @start-writing="startWriting" @login="login" @register="register" />
        <HomeHeroSection @start-writing="startWriting" @create-profile="register" />
        <HomeExploreSection />
        <div class="landing-paths">
          <HomePathSwitch :domains="landingDomains" />
          <HomeDomainSection
            v-for="domain in landingDomains"
            :key="domain.id"
            :domain="domain"
            @start-writing="startWriting"
            @create-profile="register"
          />
        </div>
        <HomePrivacyBanner />
        <HomePricingSection @start-writing="startWriting" />
        <HomeCtaSection @start-writing="startWriting" />
        <HomeFooter />
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import posthog from 'posthog-js'
import { useAuthStore } from '../stores/auth'
import ProjectsService from '../services/projects'
import type { Project } from '../types/index'
import { PROJECT_TYPES, ProjectType, type ProjectTypeValue } from '@/constants/projectTypes'
import { projectTypeLabel } from '@/utils/projectLabels'
import HomeLoadingOverlay from '@/components/home/shared/HomeLoadingOverlay.vue'
import HomeProjectTypePicker from '@/components/home/picker/HomeProjectTypePicker.vue'
import HomeNavBar from '@/components/home/landing/navbar/HomeNavBar.vue'
import HomeHeroSection from '@/components/home/landing/hero/HomeHeroSection.vue'
import HomeExploreSection from '@/components/home/landing/explore/HomeExploreSection.vue'
import HomePathSwitch from '@/components/home/landing/domains/HomePathSwitch.vue'
import HomeDomainSection from '@/components/home/landing/domains/HomeDomainSection.vue'
import HomePrivacyBanner from '@/components/home/landing/privacy/HomePrivacyBanner.vue'
import HomePricingSection from '@/components/home/landing/pricing/HomePricingSection.vue'
import HomeCtaSection from '@/components/home/landing/cta/HomeCtaSection.vue'
import HomeFooter from '@/components/home/landing/footer/HomeFooter.vue'
import { LANDING_DOMAINS } from '@/constants/landingCatalog'
import { useSeoHead } from '@/composables/useSeoHead'
import {
  organizationJsonLd,
  softwareApplicationJsonLd,
  websiteJsonLd,
} from '@/seo/prerenderHtml'

export default defineComponent({
  name: 'HomeView',

  components: {
    HomeLoadingOverlay,
    HomeProjectTypePicker,
    HomeNavBar,
    HomeHeroSection,
    HomeExploreSection,
    HomePathSwitch,
    HomeDomainSection,
    HomePrivacyBanner,
    HomePricingSection,
    HomeCtaSection,
    HomeFooter,
  },

  setup() {
    useSeoHead(() => ({
      path: '/',
      title: 'Escribonauta - Escribe tu novela sin IA leyendo tu historia',
      description:
        'Termina tu novela sin que ninguna IA lea tu manuscrito. Editor, planificación y lectores beta. Gratis para siempre. Hecho para autores hispanohablantes.',
      jsonLd: [websiteJsonLd(), organizationJsonLd(), softwareApplicationJsonLd()],
    }))

    return {
      landingDomains: LANDING_DOMAINS,
    }
  },

  data() {
    return {
      authStore: useAuthStore(),
      isLoading: false,
      showProjectTypePicker: false,
    }
  },

  mounted() {
    if (this.$route.hash) return
    this.$nextTick(() => {
      window.scrollTo(0, 0)
    })
  },

  computed: {
    projectTypeCards(): Array<{ type: ProjectTypeValue; label: string; icon: string }> {
      const icons: Record<ProjectTypeValue, string> = {
        [ProjectType.NOVEL]: 'mdi-book',
        [ProjectType.SHORT_STORY_ANTHOLOGY]: 'mdi-book-multiple',
        [ProjectType.POETRY_COLLECTION]: 'mdi-format-quote-close',
        [ProjectType.SCREENPLAY]: 'mdi-movie-open',
      }
      return PROJECT_TYPES.map((type) => ({
        type,
        label: projectTypeLabel(type),
        icon: icons[type],
      }))
    },
  },

  methods: {
    navigateToEditor(project: Project) {
      const { chapterId } = project.chapters[0]
      ;(this as any).$router.push(`/project/${project._id}?tab=editor&chapterId=${chapterId}`)
    },

    cancelProjectTypePicker() {
      this.showProjectTypePicker = false
    },

    async selectProjectType(type: ProjectTypeValue) {
      this.isLoading = true
      try {
        const project = await this.createProject(type)
        this.navigateToEditor(project)
      } catch (error) {
        console.error('Error creating project:', error)
        this.isLoading = false
      }
    },

    trackGoogleAdsConversion() {
      const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
      if (typeof gtag !== 'function') return
      gtag('event', 'conversion', {
        send_to: 'AW-11227619865/0xlzCJeQ4K4YEJnE3-kp',
      })
    },

    async startWriting() {
      if (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_POSTHOG_HOST) {
        posthog.capture('start_writing_clicked')
      }
      try {
        if (this.authStore.isLoggedIn) {
          this.isLoading = true
          const project = await this.getLastProject()
          this.isLoading = false
          if (project) {
            this.navigateToEditor(project)
            return
          }
          this.trackGoogleAdsConversion()
          this.showProjectTypePicker = true
          return
        }
        this.isLoading = true
        await this.authStore.guest()
        this.isLoading = false
        this.trackGoogleAdsConversion()
        this.showProjectTypePicker = true
      } catch (error) {
        console.error('Error starting writing:', error)
        this.isLoading = false
      }
    },

    login() {
      ;(this as any).$router.push('/login')
    },

    register() {
      ;(this as any).$router.push({ path: '/login', query: { mode: 'register' } })
    },

    async getLastProject(): Promise<Project> {
      const [lastProject] = await ProjectsService.list({}, 1, 1).then(
        (response: any) => response.data.docs,
      )
      return lastProject
    },

    async createProject(type: ProjectTypeValue): Promise<Project> {
      return ProjectsService.create({ type }).then((response: any) => response.data)
    },
  },
})
</script>

<style scoped>
.landing-page {
  --landing-nav-height: 4rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #f7f9fb 0%, #e8f4f8 100%);
}

.home-subview {
  width: 100%;
}

.home-subview--landing {
  min-height: 100vh;
  overflow-x: clip;
}

.home-subview-enter-active,
.home-subview-leave-active {
  transition:
    opacity 0.42s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.42s cubic-bezier(0.4, 0, 0.2, 1);
}

.home-subview-enter-from {
  opacity: 0;
  transform: translateY(-18px);
}

.home-subview-leave-to {
  opacity: 0;
  transform: translateY(28px);
}
</style>
